import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { isArray, isObject } from "./typeGuardUtils";

/**
 * Converts an EntityInstancePath to a string. This string includes the indices
 * that are present in the path object. The string has the form:
 * "pathPart[1]/pathPart[2]/pathPart[3]"
 * @param path
 * @returns A string representation of the given path that includes the indices
 */
export const parseToStringPathWithIndices = (path: EntityInstancePath) => {
	return path.map(element => `${element.elementName}[${element.index}]`).join("/");
};

export const parseToStringPath = (path: ModelPath | EntityInstancePath) => {
	return path.map(e => e.elementName).join("/");
};

/**
 * Function used to convert a string of the form:
 * "pathPart[1]/pathPart[2]/pathPart[3]"
 * back to a path object of type EntityInstancePath.
 * @param path
 * @returns The EntityInstancePath representation of the given string
 */
export const entityInstancePathFromString = (path: string): EntityInstancePath => {
	const matchRegex = /([^[\]]+)\[([0-9]+)]/;
	return path.split("/").flatMap(value => {
		const matchArr = value.match(matchRegex);
		return matchArr ? { elementName: matchArr[1], index: parseInt(matchArr[2]) } : [];
	});
};

export const entityInstancePathFromModelPath = (path: ModelPath, allSemantic?: boolean): EntityInstancePath => {
	return path.map(e => ({ ...e, index: allSemantic ? 0 : 1 }));
};

export const entityInstancePathFromA12Path = (path: string, allSemantic?: boolean): EntityInstancePath => {
	const modelpath = ModelPath.fromString(path);
	return entityInstancePathFromModelPath(modelpath, allSemantic);
};

export const setRepetition = (path: EntityInstancePath, pathIndex: number, repetition: number): EntityInstancePath => {
	return path.map((e, idx) => (pathIndex === idx ? { ...e, index: repetition } : e));
};

export const parseToModelPath = (path: EntityInstancePath): ModelPath => {
	return path.map(e => ({ elementName: e.elementName }));
};

/**
 * Returns the value found for a path in a document.
 * @param path the path to the target element with the format
 * [
 *    { elementName: "root" },
 *    { elementName: "genehmigungsverfahren" },
 *    { elementName: "prozessschritte" },
 *    { elementName: "Status" }
 * ].
 * @param data the document in which the path is followed.
 */
export const getValueFromModelPath = (
	path: ModelPath,
	document: Activity.Data.Document
): object | string | undefined => {
	return getValueFromEntityInstancePath(
		path.map(e => ({ ...e, index: 1 })),
		document
	);
};

type A12Document = { readonly [key: string]: any | undefined };
export type PathValueWrapper = { path: EntityInstancePath; value: object | string }[];

export const getValuesFromModelPath = (path: ModelPath, document: A12Document): PathValueWrapper => {
	return getValuesFromEntityInstancePath(entityInstancePathFromModelPath(path), document);
};

const getValuesFromEntityInstancePath = (
	path: EntityInstancePath,
	document: A12Document,
	currentIndex = 0
): PathValueWrapper => {
	if (path.length === 0) {
		return [];
	}

	const result: PathValueWrapper = [];
	const currentDocumentPart = document[path[currentIndex].elementName];

	if (currentIndex === path.length - 1 && currentDocumentPart) {
		result.push({ path: path, value: currentDocumentPart });
		return result;
	}

	if (isArray(currentDocumentPart)) {
		currentDocumentPart.forEach((e, idx) => {
			if (isObject(e)) {
				result.push(
					...getValuesFromEntityInstancePath(setRepetition(path, currentIndex, idx + 1), e, currentIndex + 1)
				);
			}
		});
	}

	if (isObject(currentDocumentPart)) {
		result.push(...getValuesFromEntityInstancePath(path, currentDocumentPart, currentIndex + 1));
	}

	return result;
};

export const getValueFromEntityInstancePath = (
	path: EntityInstancePath,
	document: A12Document
): object | string | undefined => {
	return path.reduce((documentPart, pathPart) => {
		if (!documentPart) {
			return documentPart;
		}

		if (isArray(documentPart[pathPart.elementName])) {
			return documentPart[pathPart.elementName][pathPart.index - 1];
		}

		return documentPart[pathPart.elementName];
	}, document);
};

/**
 * Erstellt einen EntityInstancePath mit den indizes 0 anhand eines "/" separierten Feld-Pfades ohne Index angaben.
 * Der EntityInstancePath kann u. a. genutzt werden, um alle tatsächlich vorhandenen EntityInstancePath mit den
 * jeweiligen indizes zu ermitteln. (siehe DocumentService.getEntityPathsInDocument)
 * @param path "/" separierter Feld-Pfad ohne Index angaben. Es wird ein führendes "/" erwartet (/root/gehehmigungsverfahren/antrag)
 */
export function createZeroIndexedEntityInstancePath(path: string): EntityInstancePath {
	return path
		.split("/")
		.map(e => ({ elementName: e, index: 0 }))
		.slice(1);
}

export function convertEntityInstancePathToModelPath(entityInstance: EntityInstancePath): string {
	return entityInstance.map(e => "/" + e.elementName).join("");
}

export const convertEntityInstancePathToString = (path: EntityInstancePath) => {
	return "/" + path.map(element => `${element.elementName}[${element.index}]`).join("/");
};

export function mergedPaths(targetPath: ModelPath, sourcePath: EntityInstancePath): EntityInstancePath {
	const finalPath: EntityInstancePath = [];
	for (let i = 0; i < targetPath.length; i++) {
		const index = i < sourcePath.length ? sourcePath[i].index : 1;
		finalPath.push({ elementName: targetPath[i].elementName, index });
	}
	return finalPath;
}

export function isSubPath(source: EntityInstancePath, target: EntityInstancePath): boolean {
	if (source.length > target.length) {
		return false;
	}
	return source.every(
		(_, idx) => source[idx].elementName === target[idx].elementName && source[idx].index === target[idx].index
	);
}
